import { CMP_VERSION, DEVICE, VIEW } from '../../views/Preview';
/* eslint-disable react/jsx-props-no-spreading */
import { ComponentChildren, Fragment, FunctionalComponent } from 'preact';
import {
  MoreItemsWrapper,
  StyledDraftButton,
  StyledHeader,
  StyledLogo,
  StyledMenu,
  StyledMenuButton,
  StyledMenuItem,
  StyledMenuItemIcon,
  StyledMenuItemText,
  StyledOptionsWrapper,
  StyledPreviewButton,
  StyledPreviewWrapper,
  StyledToolbar,
  StyledToolbarItemButton,
  VerticalSeparator,
} from './Header.styled';
import { forwardRef, useEffect, useState } from 'preact/compat';
import { ToolbarItem, useToolbarState } from 'reakit/Toolbar';

import AppSvg from '../../assets/app.svg';
import { CompositeItemOptions } from 'reakit/ts';
import LinkSvg from '../../assets/link.svg';
import LoadingBackground from '../Loading/LoadingBackground';
import Usercentricslogo from '../../assets/usercentrics-logo.svg';
import WebSvg from '../../assets/web.svg';
import { useMenuState } from 'reakit/Menu';

interface MoreItemsProps extends CompositeItemOptions {
  availableItems: string[];
  availableItemIcons?: ComponentChildren[];
  onItemChange: (t: string) => void;
  currentItem: string | undefined;
  suffix: string;
}

const MoreItems: FunctionalComponent<MoreItemsProps> = forwardRef<HTMLButtonElement, MoreItemsProps>((props, ref) => {
  const menu = useMenuState({ placement: 'bottom-end' });
  const { availableItems, availableItemIcons, onItemChange, currentItem, suffix } = props;
  const currentIndex = currentItem ? availableItems.indexOf(currentItem) : -1;

  return (
    <MoreItemsWrapper>
      <StyledMenuButton
        {...menu}
        {...props}
        ref={ref}
        aria-label={currentItem ? `${currentItem} ${suffix}` : `${suffix}`}
      >
        {availableItemIcons && <StyledMenuItemIcon>{availableItemIcons[currentIndex]}</StyledMenuItemIcon>}
        <StyledMenuItemText>{currentItem ? `${currentItem} ${suffix}` : `${suffix}`}</StyledMenuItemText>
      </StyledMenuButton>
      <StyledMenu {...menu} aria-label="More Items">
        {availableItems &&
          availableItems.length > 0 &&
          availableItems.map((item, index) => (
            <StyledMenuItem value={item} aria-label={item} {...menu} onClick={() => onItemChange(item)}>
              {availableItemIcons && <StyledMenuItemIcon>{availableItemIcons[index]}</StyledMenuItemIcon>}
              <StyledMenuItemText>{`${item} ${suffix}`}</StyledMenuItemText>
            </StyledMenuItem>
          ))}
      </StyledMenu>
    </MoreItemsWrapper>
  );
});

type HeaderProps = {
  availableVariants: string[];
  availableViews: VIEW[];
  currentView: VIEW | undefined;
  currentVariant?: string;
  device: DEVICE;
  version: string;
  availableCmpVersions: CMP_VERSION[];
  cmpVersion: string | undefined;
  isInitialized: boolean;
  isAgeVerificationEnabled: boolean;
  isDsrEnabled: boolean;
  onChangeDevice: (device: DEVICE) => void;
  onCmpVersionChange: (version: CMP_VERSION) => void;
  onLogoClick: () => void;
  onVariantChange: (abVariant: string) => void;
  onViewChange: (view: string) => void;
  onShowFirstLayer: () => void;
  onShowSecondLayer: () => void;
  onRefreshPreview: () => void;
  onShowAVGate: () => void;
  onOpenDsrForm: () => void;
  onTryOtherDevices: () => void;
};

const Header: FunctionalComponent<HeaderProps> = ({
  availableVariants,
  availableViews,
  currentView,
  currentVariant,
  device,
  version,
  availableCmpVersions,
  cmpVersion,
  isInitialized,
  isAgeVerificationEnabled,
  isDsrEnabled,
  onChangeDevice,
  onCmpVersionChange,
  onLogoClick,
  onVariantChange,
  onShowFirstLayer,
  onShowSecondLayer,
  onRefreshPreview,
  onShowAVGate,
  onOpenDsrForm,
  onTryOtherDevices,
  onViewChange,
}) => {
  const toolbar = useToolbarState();
  const [deviceType, setDeviceType] = useState<'Desktop' | 'Mobile'>('Desktop');

  useEffect(() => {
    switch (device) {
      case DEVICE.DESKTOP:
        setDeviceType('Desktop');
        break;

      default:
        setDeviceType('Mobile');
        break;
    }
  }, [device]);

  const setDevice = (value: string) => {
    if (value === 'Desktop') {
      onChangeDevice(DEVICE.DESKTOP);
      setDeviceType(value);
    } else if (value === 'Mobile') {
      onChangeDevice(DEVICE.IOS);
      setDeviceType(value);
    }
  };

  const setMobile = (value: string) => {
    if (value === 'iOS') {
      onChangeDevice(DEVICE.IOS);
    } else {
      onChangeDevice(DEVICE.ANDROID);
    }
  };

  const setCmpVersion = (value: string) => {
    switch (value) {
      case "CMP V3":
        onCmpVersionChange(CMP_VERSION.V3);
        break;
      case "CMP V2":
      default:
        onCmpVersionChange(CMP_VERSION.V2);
        break;
    }
  }

  return (
    <StyledHeader>
      <StyledLogo onClick={onLogoClick}>
        <img src={Usercentricslogo} alt="Usercentrics" />
      </StyledLogo>
      <VerticalSeparator />
      {!isInitialized && <LoadingBackground />}
      {isInitialized && (
        <StyledToolbar {...toolbar} aria-label="Header Toolbar">
          {availableCmpVersions.includes(CMP_VERSION.V2) && cmpVersion && (
            <>
            <ToolbarItem {...toolbar}>
              {(props: CompositeItemOptions) => (
                <MoreItems
                  availableItems={availableCmpVersions.map((version) => `CMP V${version.toString()}`)}
                  onItemChange={setCmpVersion}
                  currentItem={`CMP V${cmpVersion}`}
                  suffix=""
                  {...props}
                />
              )}
            </ToolbarItem>
            <VerticalSeparator />
            </>
          )}
          <ToolbarItem {...toolbar}>
            {(props: CompositeItemOptions) => (
              <MoreItems
                availableItems={['Desktop', 'Mobile']}
                availableItemIcons={[<img src={WebSvg} alt="Web" />, <img src={AppSvg} alt="App" />]}
                onItemChange={setDevice}
                currentItem={deviceType}
                suffix=""
                {...props}
              />
            )}
          </ToolbarItem>
          {deviceType === 'Mobile' && (
            <Fragment>
              <VerticalSeparator />
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <MoreItems
                    availableItems={['iOS', 'Android']}
                    onItemChange={setMobile}
                    currentItem={device === DEVICE.IOS ? 'iOS' : 'Android'}
                    suffix=""
                    {...props}
                  />
                )}
              </ToolbarItem>
            </Fragment>
          )}
          <VerticalSeparator />
          <ToolbarItem {...toolbar}>
            {(props: CompositeItemOptions) => (
              <MoreItems
                availableItems={availableViews.map((view) => view.toUpperCase())}
                onItemChange={onViewChange}
                currentItem={currentView?.toUpperCase()}
                suffix="Configuration"
                {...props}
              />
            )}
          </ToolbarItem>
          <VerticalSeparator />
          {deviceType === 'Desktop' && availableVariants.length > 0 && (
            <Fragment>
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <MoreItems
                    availableItems={['A/B Test Variant', ...availableVariants]}
                    onItemChange={onVariantChange}
                    currentItem={currentVariant ?? 'A/B Test Variant'}
                    suffix=""
                    {...props}
                  />
                )}
              </ToolbarItem>
              <VerticalSeparator />
            </Fragment>
          )}
          {deviceType === 'Desktop' ? (
            <StyledOptionsWrapper>
              {/* <StyledOptionsLabel>Options:</StyledOptionsLabel> */}
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <StyledToolbarItemButton {...props} onClick={onShowFirstLayer}>
                    Show First Layer
                  </StyledToolbarItemButton>
                )}
              </ToolbarItem>
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <StyledToolbarItemButton {...props} onClick={onShowSecondLayer}>
                    Show Second Layer
                  </StyledToolbarItemButton>
                )}
              </ToolbarItem>
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <StyledToolbarItemButton {...props} onClick={onRefreshPreview}>
                    Refresh Preview
                  </StyledToolbarItemButton>
                )}
              </ToolbarItem>
              {isAgeVerificationEnabled && (
                <ToolbarItem {...toolbar}>
                  {(props: CompositeItemOptions) => (
                    <StyledToolbarItemButton {...props} onClick={onShowAVGate}>
                      Show Age Verification
                    </StyledToolbarItemButton>
                  )}
                </ToolbarItem>
              )}
              {isDsrEnabled && (
                <ToolbarItem {...toolbar}>
                  {(props: CompositeItemOptions) => (
                    <StyledToolbarItemButton {...props} onClick={onOpenDsrForm}>
                      Data Subject Request
                    </StyledToolbarItemButton>
                  )}
                </ToolbarItem>
              )}
            </StyledOptionsWrapper>
          ) : (
            <StyledOptionsWrapper>
              <ToolbarItem {...toolbar}>
                {(props: CompositeItemOptions) => (
                  <StyledToolbarItemButton {...props} onClick={onTryOtherDevices}>
                    Try on other devices&nbsp;&nbsp;
                    <img src={LinkSvg} alt="link" />
                  </StyledToolbarItemButton>
                )}
              </ToolbarItem>
            </StyledOptionsWrapper>
          )}
          <StyledPreviewWrapper>
            {version === 'preview' ? (
              <StyledDraftButton>Draft Preview</StyledDraftButton>
            ) : (
              <StyledPreviewButton>Live Preview</StyledPreviewButton>
            )}
          </StyledPreviewWrapper>
        </StyledToolbar>
      )}
    </StyledHeader>
  );
};

export default Header;
